/**
 * Язык страницы задаёт вызывающая сторона query-параметром `lang` — своего
 * переключателя у хелпдеска нет. Вызывают четверо, и коды у них разные:
 *
 *   веб (next-intl useLocale)                    ru        uz-Latn      uz-Cyrl
 *   iOS (QCBackend.locale(from:))                ru-RU     uz-UZ        uz-Cyrl-UZ
 *   Android (LanguageMapper.mapToNetworkLang)    ru-RU     uz-UZ        uz-Cyrl-UZ
 *
 * Мобильный набор с регионом уже в проде и общий с бэком (Accept-Language,
 * User.language), поэтому подстраивается хелпдеск, а не четыре клиента.
 *
 * Разбор нестрогий (регистр, `_` вместо `-`) намеренно: строгая сверка не
 * «ловила бы кривой вызов», а молча подставляла бы чужой язык — `ru-RU`
 * когда-то отдавал русскоязычному пользователю узбекскую форму.
 */
export const SUPPORTED_LANGS = ['ru', 'uz-Latn', 'uz-Cyrl'] as const;

export type Lang = (typeof SUPPORTED_LANGS)[number];

/** Домен узбекского стенда: helpdesk.q-nazorat.uz. */
const UZ_STAND_DOMAIN = 'q-nazorat.uz';

/**
 * Дефолт для клиента, не приславшего `lang` (прямое открытие в браузере или
 * версия приложения до того, как мобилки научились передавать язык).
 *
 * Считается по хосту, а не задаётся сборкой: `docker:release` собирает ОДИН
 * образ, который деплоится и на РФ-стенд, и на узбекский — они различаются
 * только ingress'ом в helm-values. Зашитый в сборку дефолт означал бы либо
 * два образа, либо ветку, которую нельзя мержить в main (и узбекскую форму
 * на РФ-инстансе после первого же мержа). Хост — единственное, что честно
 * отличает стенды в рантайме; в основном вебе стенд определяется так же
 * (`getHelpdeskEnv`).
 */
export function defaultLangForHost(hostname: string): Lang {
  const host = hostname.toLowerCase();
  // Именно суффикс, а не «содержит»: иначе q-nazorat.attacker.example
  // выдавал бы себя за наш стенд.
  const isUzStand = host === UZ_STAND_DOMAIN || host.endsWith(`.${UZ_STAND_DOMAIN}`);

  return isUzStand ? 'uz-Latn' : 'ru';
}

export const DEFAULT_LANG: Lang = defaultLangForHost(window.location.hostname);

/**
 * Приводит код локали любого клиента к внутреннему. Возвращает null, если код
 * не наш, — вызывающий сам решает, что делать (в резолве это уход в дефолт).
 */
export function normalizeLang(value: string | null | undefined): Lang | null {
  if (!value) return null;

  const tag = value.trim().toLowerCase().replace(/_/g, '-');

  if (tag === 'ru' || tag.startsWith('ru-')) return 'ru';

  // Кириллица проверяется до латиницы: 'uz-cyrl-uz' подходит под оба условия,
  // и обратный порядок отдал бы латиницу вместо выбранной пользователем
  // кириллицы. Голый 'uz' у нас не встречается, но по традиции латиница.
  if (tag.includes('cyrl')) return tag.startsWith('uz') ? 'uz-Cyrl' : null;
  if (tag === 'uz' || tag.startsWith('uz-')) return 'uz-Latn';

  return null;
}

export const resolveLang = (search: string): Lang =>
  normalizeLang(new URLSearchParams(search).get('lang')) ?? DEFAULT_LANG;
